// Inline Query / Clarification dialog used inside an approval row.
// Lets an approver pick a recipient from the approval-flow user list and
// raise a clarification query that parks the request until answered.

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { HelpCircle, Send, X } from "lucide-react";
import UserLabel, { useUserDirectory } from "./UserLabel";
import { SearchableSelect } from "./SearchableSelect";

const recipientLabel = (u) =>
  `${u.email} ${u.is_creator ? "· (Creator)" : u.via_project ? `· Project ${u.via_project}` : `· L${u.level}`}${u.role ? ` · ${u.role}` : ""}`;

export function QueryRaiseInline({ requestId, onRaised, onClose }) {
  const [eligible, setEligible] = useState([]);
  const [toUserId, setToUserId] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/approvals/${requestId}/eligible-responders`)
      .then(r => {
        setEligible(r.data || []);
        if ((r.data || []).length > 0) setToUserId(r.data[0].user_id);
      })
      .catch(() => toast.error("Could not load eligible users"));
  }, [requestId]);

  async function submit() {
    if (!toUserId) { toast.error("Pick a user to query"); return; }
    if (!message.trim()) { toast.error("Type your query message"); return; }
    setBusy(true);
    try {
      await api.post(`/approvals/${requestId}/query`, { to_user_id: toUserId, message: message.trim() });
      toast.success("Query raised — voucher moved to the Hold (Query) tab until clarified");
      if (onRaised) onRaised();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not raise query");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 border border-amber-300 bg-amber-50 p-3" data-testid={`query-raise-${requestId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs font-bold text-amber-900 uppercase tracking-wider">
          <HelpCircle size={14} /> Raise Clarification Query
        </div>
        <button onClick={onClose} className="p-1 hover:bg-amber-100 text-amber-700" data-testid={`query-raise-close-${requestId}`}>
          <X size={14} />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr,2fr] gap-2 text-xs">
        {/* Jun-2026 (user request) — type-to-search recipient picker. */}
        <SearchableSelect
          testid={`query-to-user-${requestId}`}
          value={toUserId}
          items={eligible}
          onChange={(id) => setToUserId(id || "")}
          getKey={(u) => u.user_id}
          getLabel={recipientLabel}
          getSearchText={(u) => `${u.email || ""} ${u.role || ""} ${u.via_project || ""}`.toLowerCase()}
          placeholder="— Select recipient —"
          searchPlaceholder="Type name, email, role or project…"
          inputClassName="px-2 py-2 border border-slate-300 bg-white focus:border-amber-500 focus:outline-none text-xs w-full"
        />
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={2}
                  placeholder="Clearly describe what clarification you need before approving…"
                  data-testid={`query-message-${requestId}`}
                  className="px-2 py-2 border border-slate-300 bg-white focus:border-amber-500 focus:outline-none" />
      </div>
      <div className="mt-2 flex justify-end">
        <button onClick={submit} disabled={busy}
                data-testid={`query-submit-${requestId}`}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-60">
          <Send size={13} /> {busy ? "Sending…" : "Send Query"}
        </button>
      </div>
    </div>
  );
}

/* Response form — shown to the user the query is addressed to. */
export function QueryRespondInline({ requestId, query, onResponded }) {
  const [response, setResponse] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!response.trim()) { toast.error("Type your clarification"); return; }
    setBusy(true);
    try {
      await api.post(`/approvals/${requestId}/query/${query.id}/respond`, { response: response.trim() });
      toast.success("Clarification submitted — voucher routed back to approver");
      if (onResponded) onResponded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit response");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 border border-blue-300 bg-blue-50 p-3" data-testid={`query-respond-${query.id}`}>
      <div className="flex items-center gap-2 text-xs font-bold text-blue-900 uppercase tracking-wider mb-2">
        <HelpCircle size={14} /> Clarification Requested by <UserLabel value={query.raised_by_email} fallback="—" className="ml-1" /> (L{query.raised_at_level})
      </div>
      <div className="bg-white border border-blue-200 p-2 text-xs text-slate-700 mb-2">
        <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700 mb-1">Query</div>
        <div className="whitespace-pre-wrap break-words">{query.message}</div>
      </div>
      <textarea value={response} onChange={e => setResponse(e.target.value)} rows={3}
                placeholder="Write your clarification here…"
                data-testid={`query-response-text-${query.id}`}
                className="w-full px-2 py-2 border border-slate-300 bg-white focus:border-blue-500 focus:outline-none text-xs" />
      <div className="mt-2 flex justify-end">
        <button onClick={submit} disabled={busy}
                data-testid={`query-respond-submit-${query.id}`}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-60">
          <Send size={13} /> {busy ? "Submitting…" : "Submit Clarification"}
        </button>
      </div>
    </div>
  );
}

/* Complete query thread shown in expanded view of every approval row. */
export function QueryHistory({ queries }) {
  useUserDirectory();
  if (!queries || queries.length === 0) return null;
  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid="query-history">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2 flex items-center gap-1">
        <HelpCircle size={11} /> Queries · {queries.length}
      </div>
      <div className="space-y-2">
        {queries.map(q => (
          <div key={q.id} className={`border p-2 text-[11px] ${q.status === "Open" ? "border-amber-300 bg-amber-50/60" : "border-slate-200 bg-slate-50/60"}`}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${q.status === "Open" ? "bg-amber-200 text-amber-900" : "bg-slate-300 text-slate-700"}`}>{q.status}</span>
              <span className="font-bold text-slate-800"><UserLabel value={q.raised_by_email} fallback="—" /></span>
              <span className="text-slate-400">(L{q.raised_at_level})</span>
              <span className="text-slate-500">→</span>
              <span className="font-bold text-slate-800"><UserLabel value={q.raised_to_email} fallback="—" /></span>
              <span className="text-slate-400">· {new Date(q.raised_at).toLocaleString()}</span>
            </div>
            <div className="mt-1 text-slate-700 italic break-words">"{q.message}"</div>
            {q.response && (
              <div className="mt-2 pt-2 border-t border-slate-200">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-emerald-200 text-emerald-900">Response</span>
                  <span className="font-bold text-slate-800"><UserLabel value={q.responded_by_email} fallback="—" /></span>
                  <span className="text-slate-400">· {q.responded_at ? new Date(q.responded_at).toLocaleString() : ""}</span>
                </div>
                <div className="mt-1 text-slate-700 break-words">"{q.response}"</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
